import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase/client';
import { BillingStatus } from '../types';

interface UseBillingStatusReturn {
  billingStatus: BillingStatus | null;
  isLoading: boolean;
  error: string | null;
  refreshBillingStatus: () => Promise<void>;
}

export const useBillingStatus = (): UseBillingStatusReturn => {
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  console.log('🔍 useBillingStatus: Hook state', { isLoading, hasBillingStatus: !!billingStatus, hasError: !!error });

  const fetchBillingStatus = useCallback(async () => {
    console.log('🔍 useBillingStatus: fetchBillingStatus called');
    try {
      setIsLoading(true);
      setError(null);

      // Get current user with session fallback
      let user = null;
      let userError = null;
      
      try {
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        user = authUser;
        userError = authError;
      } catch (fetchError) {
        // Fallback to session if getUser fails
        console.log('getUser failed, trying getSession:', fetchError);
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        user = session?.user || null;
        userError = sessionError;
      }
      
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Store user ID for real-time subscriptions
      setUserId(user.id);
      
      // Check user role from auth metadata
      const userRole = user.user_metadata?.role || 'user';
      
      // Special handling for CSR and Admin users
      if (userRole === 'csr' || userRole === 'admin') {
        console.log('CSR/Admin user detected in billing hook, using default values');
        const status: BillingStatus = {
          planType: userRole === 'csr' ? 'CSR Account' : 'Admin Account',
          billingStatus: 'internal',
          exportQuotaRemaining: 0,
          stripeCustomerId: null,
          nextBillingDate: null,
          paymentMethod: null,
          subscriptionId: null,
        };
        setBillingStatus(status);
        setIsLoading(false);
        console.log('🔍 useBillingStatus: CSR/Admin - setIsLoading(false) called');
        return;
      }

      // Fetch user billing information for regular users
      const { data: userData, error: userDataError } = await supabase
        .from('users')
        .select(`
          id,
          plan_type,
          billing_status,
          export_quota_remaining,
          stripe_customer_id,
          next_billing_date,
          subscription_id
        `)
        .eq('id', user.id)
        .single();

      if (userDataError) {
        // If user doesn't exist yet, return default values instead of throwing
        if (userDataError.code === 'PGRST116') {
          console.log('User record not found, using default billing status');
          const status: BillingStatus = {
            planType: null,
            billingStatus: 'unpaid',
            exportQuotaRemaining: 0,
            stripeCustomerId: null,
            nextBillingDate: null,
            paymentMethod: null,
            subscriptionId: null,
          };
          setBillingStatus(status);
          setIsLoading(false);
          return;
        }
        throw new Error(`Failed to fetch user data: ${userDataError.message}`);
      }

      if (!userData) {
        throw new Error('User data not found');
      }

      // TODO: Fetch payment method from Stripe if needed
      // For now, we'll set it to null
      const paymentMethod = null;

      const status: BillingStatus = {
        planType: userData.plan_type,
        billingStatus: userData.billing_status || 'unpaid',
        exportQuotaRemaining: userData.export_quota_remaining || 0,
        stripeCustomerId: userData.stripe_customer_id,
        nextBillingDate: userData.next_billing_date,
        paymentMethod,
        subscriptionId: userData.subscription_id,
      };

      setBillingStatus(status);
      
      // Sentry tracking removed (Sentry disabled)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch billing status';
      setError(errorMessage);
      console.error('Billing status fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshBillingStatus = useCallback(async () => {
    await fetchBillingStatus();
  }, [fetchBillingStatus]);

  useEffect(() => {
    fetchBillingStatus();
  }, [fetchBillingStatus]);

  // Set up real-time subscription for quota changes
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('billing-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `id=eq.${userId}`,
        },
        (payload) => {
          console.log('Billing status updated:', payload);
          // Update local state with new data
          if (payload.new) {
            setBillingStatus(prev => prev ? {
              ...prev,
              planType: payload.new.plan_type,
              billingStatus: payload.new.billing_status || 'unpaid',
              exportQuotaRemaining: payload.new.export_quota_remaining || 0,
              stripeCustomerId: payload.new.stripe_customer_id,
              nextBillingDate: payload.new.next_billing_date,
              subscriptionId: payload.new.subscription_id,
            } : null);
          }
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [userId]);

  return {
    billingStatus,
    isLoading,
    error,
    refreshBillingStatus,
  };
};